import React, { useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '../../../../contexts/UserContext';
import { useViewerStore } from '../../../../store/store';
import { useGetSaveDesignData } from '../../../../hooks/useGetSaveDesignData';
import { useSiteCalculationBlockedStatus } from '../../../../hooks/calculationStateHook';
import {
    PRODUCTION_CALCULATION_TYPES,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from '../../../../services/siteCalculationState';
import { logger } from '../../../../services/loggingService';
import {
    beginFinalizeModuleGraph,
    endFinalizeModuleGraph,
} from '../Three-JS-Viewer/Controllers/TreeImpactEligibility';
import {
    PCI_ONEDRAW_FINALIZE_SAVE_DONE_MSG,
    PCI_ONEDRAW_FINALIZE_SAVE_MSG,
    PCI_ONEDRAW_FINALIZE_SNAPSHOT_MSG,
    PCI_ONEDRAW_FINALIZE_SNAPSHOT_RESULT_MSG,
    type PciFinalizeSaveInput,
    type PciFinalizeSnapshot,
} from '../../../../pre-contract/lib/preContractEmbedBridge';
import { collectFinalizeGateIssues } from './startFinalizeSaveFlow';
import { runViewportFinalizeSave } from './runViewportFinalizeSave';

const FINALIZE_CALC_WAIT_MS = 120_000;

async function waitWhile(predicate: () => boolean, maxMs = FINALIZE_CALC_WAIT_MS): Promise<void> {
    const started = Date.now();
    while (predicate()) {
        if (Date.now() - started > maxMs) return;
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
}

function sanitizeRsaNotesInput(value: string, maxLength = 255): string {
    return value.replace(/[\p{Cf}\u0000-\u001F\u007F]|[^\p{L}\p{N}\s.,'\-:()/]/gu, '').slice(0, maxLength);
}

function buildSnapshot(
    ready: boolean,
    readyMessage: string | null,
    hasCapability: (name: string) => boolean,
    saveInput: PciFinalizeSaveInput,
): PciFinalizeSnapshot {
    const state = useViewerStore.getState();
    return {
        ready,
        readyMessage,
        gateIssues: collectFinalizeGateIssues(hasCapability),
        setbackNotes: String(state.setbackNotes ?? ''),
        detachedStructureBuildingIds: Array.isArray(state.detachedStructureBuildingIds)
            ? state.detachedStructureBuildingIds
            : [],
        isInActiveDesign: Boolean(state.isInActiveDesign),
        designID: Number(state.designID) || 0,
        rsaNotes: saveInput.rsaNotes,
        rsaStatus: saveInput.rsaStatus,
        customerSiteRestrictions: String(state.customerSiteRestrictions ?? ''),
        annualUsage: saveInput.annualUsage,
        epcEstimatedProduction: saveInput.epcEstimatedProduction,
    };
}

/** Hidden host in the viewport embed that prepares and saves designs for pre-contract Finalize. */
export const PciFinalizeBridge: React.FC = () => {
    const { hasCapability } = useCurrentUser();
    const loadingCompleted = useViewerStore((s) => s.loadingCompleted);
    const refreshingShade = useViewerStore((s) => s.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((s) => s.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((s) => s.shadeCalculatingLength);
    const rsaNotesFromDesign = useViewerStore((s) => s.rsaNotesFromDesign);
    const annualUsage = useViewerStore((s) => s.annualUsage);
    const epcEstimatedProduction = useViewerStore((s) => s.epcEstimatedProduction);

    const [saveInput, setSaveInput] = useState<PciFinalizeSaveInput>({
        rsaNotes: sanitizeRsaNotesInput(String(rsaNotesFromDesign ?? '')),
        customerSiteRestrictions: '',
        annualUsage: annualUsage != null && Number(annualUsage) > 0 ? Number(annualUsage) : null,
        epcEstimatedProduction:
            epcEstimatedProduction != null && Number(epcEstimatedProduction) > 0
                ? Number(epcEstimatedProduction)
                : null,
    });
    const [finalizePrepReady, setFinalizePrepReady] = useState(false);
    const pendingSaveRef = useRef<{ requestId?: string; input: PciFinalizeSaveInput } | null>(null);

    const getPayload = useGetSaveDesignData(
        saveInput.annualUsage,
        saveInput.epcEstimatedProduction,
        saveInput.rsaStatus,
        saveInput.rsaNotes,
    );

    const calculationBlockedMessage = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady: finalizePrepReady,
        shadeCalculatingIdx: shadeCalculatingIdx + 1,
        shadeCalculatingLength,
    });

    useEffect(() => {
        if (!loadingCompleted) return;
        let cancelled = false;
        const runFinalizePrep = async () => {
            beginFinalizeModuleGraph();
            try {
                siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                await siteCalculationState.useCalculations(SHADE_CALCULATIONS);
                await waitWhile(() => useViewerStore.getState().refreshingShade);
                await waitWhile(
                    () =>
                        PRODUCTION_CALCULATION_TYPES.some((type) =>
                            siteCalculationState.doesNeedUpdateOrUpdating(type),
                        ) || siteCalculationState.doesNeedUpdateOrUpdating(SHADE_CALCULATIONS),
                );
            } catch (err) {
                logger.error(err, 'Pre-contract finalize calculation prep failed');
            } finally {
                endFinalizeModuleGraph();
                if (!cancelled) setFinalizePrepReady(true);
            }
        };
        setFinalizePrepReady(false);
        void runFinalizePrep();
        return () => {
            cancelled = true;
            endFinalizeModuleGraph();
        };
    }, [loadingCompleted]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                requestId?: string;
            } & Partial<PciFinalizeSaveInput>;
            if (!data?.type) return;

            if (data.type === PCI_ONEDRAW_FINALIZE_SNAPSHOT_MSG) {
                try {
                    window.parent.postMessage(
                        {
                            type: PCI_ONEDRAW_FINALIZE_SNAPSHOT_RESULT_MSG,
                            ...buildSnapshot(
                                finalizePrepReady && !refreshingShade,
                                calculationBlockedMessage,
                                hasCapability,
                                saveInput,
                            ),
                        },
                        window.location.origin,
                    );
                } catch {
                    /* ignore */
                }
                return;
            }

            if (data.type === PCI_ONEDRAW_FINALIZE_SAVE_MSG) {
                const next: PciFinalizeSaveInput = {
                    rsaStatus: data.rsaStatus,
                    rsaNotes: sanitizeRsaNotesInput(String(data.rsaNotes ?? '')),
                    customerSiteRestrictions: String(data.customerSiteRestrictions ?? ''),
                    annualUsage: data.annualUsage ?? null,
                    epcEstimatedProduction: data.epcEstimatedProduction ?? null,
                    acceptEfficiencyOverride: data.acceptEfficiencyOverride,
                };
                pendingSaveRef.current = { requestId: data.requestId, input: next };
                setSaveInput(next);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [calculationBlockedMessage, finalizePrepReady, hasCapability, refreshingShade, saveInput]);

    useEffect(() => {
        const pending = pendingSaveRef.current;
        if (!pending) return;
        pendingSaveRef.current = null;
        void runViewportFinalizeSave({
            input: pending.input,
            hasCapability,
            getPayload,
        }).then((result) => {
            try {
                window.parent.postMessage(
                    {
                        type: PCI_ONEDRAW_FINALIZE_SAVE_DONE_MSG,
                        requestId: pending.requestId,
                        ...result,
                    },
                    window.location.origin,
                );
            } catch {
                /* ignore */
            }
        });
    }, [getPayload, hasCapability, saveInput]);

    return null;
};
